import { Link } from 'react-router-dom';
import { VacancyActions, VacancyActionDialog } from '../components/VacancyActions';
import { JobLinkActions } from '../components/JobLinkActions';
import { useState } from 'react';

import { BriefcaseBusiness, Eye, FileUp, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';

import type { Vacancy, VacancyAction, VacancyState } from '@/api/schema';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Chip } from '@/components/ui/Chip';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { can } from '@/lib/permissions';
import { cx } from '@/lib/cx';
import { useDebounced } from '@/lib/useDebounced';

import { useCareersOptions, useVacancies, type VacancyFilters } from '../api';
import { VacancyForm } from '../components/VacancyForm';
import { VacancyImportModal } from '../components/VacancyImportModal';
import { VacancyIconButton } from '../components/VacancyIconButton';
import { VacancyBulkDeleteDialog } from '../components/VacancyBulkDeleteDialog';
import styles from './CareersPage.module.css';

const VACANCY_STATE_LABEL: Record<VacancyState, string> = {
  draft: 'Draft',
  published: 'Published',
  closed: 'Inactive',
};

export function VacancyDetails({ job }: { job: Vacancy }) {
  return (
    <div className={styles.details}>
      <p className={styles.meta}>{[job.location, job.country].filter(Boolean).join(' · ')}</p>
      <dl className={styles.facts}>
        <div>
          <dt>Industry</dt>
          <dd>{job.industry}</dd>
        </div>
        <div>
          <dt>Employment</dt>
          <dd>{job.employment_type}</dd>
        </div>
        <div>
          <dt>Salary</dt>
          <dd>{job.salary || 'To be discussed'}</dd>
        </div>
      </dl>
      <div>
        <h3>Job description</h3>
        <p className={styles.prose}>{job.description}</p>
      </div>
      {job.requirements ? (
        <div>
          <h3>Experience and requirements</h3>
          <p className={styles.prose}>{job.requirements}</p>
        </div>
      ) : null}
    </div>
  );
}

export function CareersPage() {
  const { data: user } = useCurrentUser();
  const [filters, setFilters] = useState<VacancyFilters>({
    page: 1,
    search: '',
    country: '',
    industry: '',
    state: '',
  });
  const search = useDebounced(filters.search, 250);
  const jobs = useVacancies({ ...filters, search });
  const options = useCareersOptions();
  const [editing, setEditing] = useState<Vacancy | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [viewing, setViewing] = useState<Vacancy | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState<{
    jobs: Vacancy[];
    total: number;
  } | null>(null);
  const selectableJobs =
    jobs.data?.items.filter((job) =>
      job.actions?.some(({ action, allowed }) => action === 'delete' && allowed),
    ) ?? [];
  const selectedJobs = selectableJobs.filter((job) => selectedIds.includes(job.id));
  const allSelected =
    selectableJobs.length > 0 && selectedJobs.length === selectableJobs.length;
  const selectionReady = !jobs.isFetching && filters.search === search;
  const [selectedAction, setSelectedAction] = useState<{
    job: Vacancy;
    action: VacancyAction['action'];
    lastOnPage: boolean;
  } | null>(null);
  const filter = (values: Partial<VacancyFilters>) => {
    setSelectedIds([]);
    setFilters((current) => ({ ...current, ...values, page: 1 }));
  };
  return (
    <Page>
      <PageHeader
        title="Vacancies"
        description="Create vacancies, publish opportunities, and keep available jobs up to date."
        actions={
          can(user, 'careers', 'create') ? (
            <>
              <Button
                variant="ghost"
                icon={<FileUp size={16} />}
                onClick={() => {
                  setImporting(true);
                }}
              >
                Import vacancies
              </Button>
              <Button
                icon={<Plus size={16} />}
                onClick={() => {
                  setEditing('new');
                }}
              >
                Create vacancy
              </Button>
            </>
          ) : null
        }
      />
      <p>
        <Link to="/jobs" target="_blank" rel="noreferrer">
          Open public Careers page ↗
        </Link>{' '}
        · Published vacancies appear here automatically.
      </p>
      <Panel>
        <div className={styles.filters}>
          <Input
            type="search"
            aria-label="Search vacancies"
            placeholder="Search by job title or location"
            value={filters.search}
            onChange={(event) => {
              filter({ search: event.target.value });
            }}
          />
          <Select
            aria-label="Filter by country"
            placeholder="All countries"
            value={filters.country}
            options={(options.data?.countries ?? []).map((value) => ({ value, label: value }))}
            onChange={(event) => {
              filter({ country: event.target.value });
            }}
          />
          <Select
            aria-label="Filter by industry"
            placeholder="All industries"
            value={filters.industry}
            options={(options.data?.industries ?? []).map((value) => ({ value, label: value }))}
            onChange={(event) => {
              filter({ industry: event.target.value });
            }}
          />
          <Select
            aria-label="Filter by visibility"
            placeholder="All vacancies"
            value={filters.state}
            options={Object.entries(VACANCY_STATE_LABEL).map(([value, label]) => ({
              value,
              label,
            }))}
            onChange={(event) => {
              filter({ state: event.target.value as VacancyFilters['state'] });
            }}
          />
        </div>
      </Panel>
      {jobs.isPending ? (
        <Spinner label="Loading vacancies" />
      ) : jobs.isError ? (
        <ErrorState
          message="The vacancies did not load."
          onRetry={() => {
            void jobs.refetch();
          }}
        />
      ) : jobs.data.total === 0 ? (
        <EmptyState
          icon={<BriefcaseBusiness size={22} />}
          title="No vacancies found"
          body="Create a vacancy, or change the filters to see more opportunities."
        />
      ) : (
        <>
          {selectableJobs.length > 0 ? (
            <div className={styles.bulkBar}>
              <Checkbox
                label="Select all on this page"
                checked={allSelected}
                indeterminate={selectedJobs.length > 0 && !allSelected}
                disabled={!selectionReady}
                onChange={(event) => {
                  setSelectedIds(
                    event.target.checked ? selectableJobs.map((job) => job.id) : [],
                  );
                }}
              />
              {selectedJobs.length > 0 ? (
                <div className={styles.bulkActions}>
                  <span role="status">{selectedJobs.length} selected</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedIds([]);
                    }}
                  >
                    Clear selection
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={<Trash2 size={16} aria-hidden="true" />}
                    disabled={!selectionReady}
                    onClick={() => {
                      setBulkDeleting({ jobs: selectedJobs, total: jobs.data.total });
                    }}
                  >
                    Delete selected ({selectedJobs.length})
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className={styles.grid}>
            {jobs.data.items.map((job) => {
              const deletion = job.actions?.find(({ action }) => action === 'delete');
              const selected = Boolean(deletion?.allowed && selectedIds.includes(job.id));
              return (
                <article
                  key={job.id}
                  className={cx(styles.card, selected && styles.selectedCard)}
                >
                  <div className={styles.cardTop}>
                    <div className={styles.cardSelection}>
                      {deletion ? (
                        <Checkbox
                          aria-label={`Select ${job.title}`}
                          checked={selected}
                          disabled={!deletion.allowed || !selectionReady}
                          onChange={(event) => {
                            setSelectedIds((current) =>
                              event.target.checked
                                ? [...current, job.id]
                                : current.filter((id) => id !== job.id),
                            );
                          }}
                        />
                      ) : null}
                      <span className={styles.industry}>{job.industry}</span>
                    </div>
                    <Chip tone={job.state === 'published' ? 'approved' : 'neutral'}>
                      {VACANCY_STATE_LABEL[job.state]}
                    </Chip>
                  </div>
                  <h2>{job.title}</h2>
                  {job.highlighted ? <Chip tone="neutral">Highlighted</Chip> : null}
                  <p className={styles.meta}>
                    <MapPin size={14} aria-hidden="true" />
                    {[job.location, job.country].filter(Boolean).join(', ')}
                  </p>
                  <p className={styles.salary}>{job.salary || 'Salary to be discussed'}</p>
                  <p className={styles.meta}>{job.employment_type}</p>
                  <div className={styles.cardActions}>
                    {job.state === 'published' ? <JobLinkActions job={job} iconOnly /> : null}
                    <VacancyIconButton
                      label="View role"
                      icon={<Eye size={18} aria-hidden="true" />}
                      onClick={() => {
                        setViewing(job);
                      }}
                    />
                    {can(user, 'careers', 'edit') ? (
                      <VacancyIconButton
                        label="Edit"
                        icon={<Pencil size={18} aria-hidden="true" />}
                        onClick={() => {
                          setEditing(job);
                        }}
                      />
                    ) : null}
                    <VacancyActions
                      job={job}
                      onSelect={(action) => {
                        setSelectedAction({
                          job,
                          action,
                          lastOnPage: jobs.data.items.length === 1,
                        });
                      }}
                    />
                  </div>
                </article>
              );
            })}
          </div>
          <Pagination
            page={filters.page}
            perPage={12}
            total={jobs.data.total}
            label="vacancies"
            onChange={(page) => {
              setSelectedIds([]);
              setFilters((current) => ({ ...current, page }));
            }}
          />
        </>
      )}
      {bulkDeleting ? (
        <VacancyBulkDeleteDialog
          jobs={bulkDeleting.jobs}
          onClose={() => {
            setBulkDeleting(null);
          }}
          onSuccess={(affected) => {
            setSelectedIds([]);
            const lastPage = Math.max(1, Math.ceil((bulkDeleting.total - affected) / 12));
            setFilters((current) => ({ ...current, page: Math.min(current.page, lastPage) }));
          }}
        />
      ) : null}
      {selectedAction ? (
        <VacancyActionDialog
          job={selectedAction.job}
          action={selectedAction.action}
          onClose={() => {
            setSelectedAction(null);
          }}
          onSuccess={() => {
            if (
              selectedAction.lastOnPage &&
              filters.page > 1 &&
              (selectedAction.action === 'delete' || filters.state)
            ) {
              setFilters((current) => ({ ...current, page: current.page - 1 }));
            }
          }}
        />
      ) : null}
      {importing ? (
        <VacancyImportModal
          onClose={() => {
            setImporting(false);
          }}
        />
      ) : null}
      {editing ? (
        <VacancyForm
          key={editing === 'new' ? 'new' : editing.id}
          vacancy={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
      {viewing ? (
        <Modal
          open
          size="lg"
          title={viewing.title}
          onClose={() => {
            setViewing(null);
          }}
        >
          <VacancyDetails job={viewing} />
        </Modal>
      ) : null}
    </Page>
  );
}
